<script setup lang="ts">
import api from "@/api";
import { baseURL } from "@/api/config";
import type { TDriver, TRide } from "@/assets/js/globalTypes";
import { debounce, rideStatusText } from "@/assets/js/helpers";
import { useRoute, useRouter } from "vue-router";
import driverAltIcon from "@/assets/image/default-driver.png";
import carAltIcon from "@/assets/image/default-car.png";

import TableRow from "../taxi/tableRow.vue";
import NewModernInput from "@/components/input/NewModernInput.vue";

definePage({
	name: "RidesPage",
	meta: {
		requiresLogin: true,
	},
});

const pageLimit = 8;

const rides = ref<(TRide & Partial<TDriver>)[]>([]);
const count = ref<number>(0);
const search = ref("");

const route = useRoute();
const router = useRouter();

const pageCount = computed(() => {
	return Math.ceil(count.value / pageLimit);
});

const currentPage = computed(() => {
	return Number(route.query.page) || 1;
});
onBeforeRouteLeave((to) => {
	if (to.query.page !== undefined) {
		const { page, ...restQuery } = to.query;
		return { path: to.path, query: restQuery };
	}
});
const handleInput = debounce(repopulateRides, 300, false);

onMounted(() => {
	getRides();
});

async function getRides(limit = pageLimit, offset = 0) {
	try {
		const res = await api.getRides({
			limit,
			offset,
			search: search.value.trim() || null,
		});

		rides.value = res.data.data.data;
		count.value = res.data.data.meta_info.count;
	} catch (error) {
		console.log(error);
	}
}

function handlePageChange(pageNumber: number) {
	const offset = pageLimit * (pageNumber - 1);

	getRides(pageLimit, offset);
}

function repopulateRides() {
	router.push({
		name: "RidesPage",
	});

	getRides(pageLimit, 0);
}

function openRide(rideId: number) {
	router.push(`/rides/${rideId}`);
}

function isFinishedRide(ride: TRide) {
	return Number(ride.rid_status) === 2;
}
</script>

<template>
	<div id="ride-page">
		<div class="content">
			<div class="control-bar">
				<div class="filters"></div>

				<div class="search-wrap">
					<new-modern-input
						v-model="search"
						:label="''"
						:bind="{
							type: 'text',
							placeholder: $t('app.labels.search'),
						}"
						@input="handleInput"
					/>
				</div>
			</div>

			<div
				v-if="rides.length > 0"
				class="table-wrapper"
			>
				<table class="rides-table">
					<thead>
						<tr>
							<th>ID</th>

							<th>
								{{ $t("app.driver.status") }}
							</th>

							<th>
								{{ $t("app.rides.adjusted-price") }}
							</th>

							<th>
								{{ $t("app.driver.driver") }}
							</th>

							<th>
								{{ $t("app.driver.car") }}
							</th>

							<th>
								{{ $t("app.driver.plates") }}
							</th>

							<th>
								{{ $t("app.rides.comment") }}
							</th>
						</tr>
					</thead>

					<tbody>
						<table-row
							v-for="ride in rides"
							:key="ride.rid_id"
							@click="openRide(ride.rid_id)"
						>
							<td>
								{{ ride.rid_id }}
							</td>

							<td>
								{{ rideStatusText(ride.rid_status) }}
							</td>

							<td>
								<span v-if="isFinishedRide(ride)">
									{{ ride.rid_adjusted_price }} RSD
								</span>

								<span v-else>-</span>
							</td>

							<td>
								<div class="driver-info">
									<div class="table-image">
										<img
											:src="ride.image ? baseURL + ride.image : driverAltIcon"
											alt="Auto"
										/>
									</div>

									<span class="driver-name">
										<template v-if="ride.tax_id">
											{{ ride.tax_name || "" }}
											{{ ride.tax_lastname || "" }}
										</template>

										<template v-else>-</template>
									</span>
								</div>
							</td>

							<td>
								<div class="vehicle-info">
									<div class="table-image vehicle-image">
										<img
											:src="
												ride.tax_car_image
													? baseURL + ride.tax_car_image
													: carAltIcon
											"
											:alt="ride.tax_model || 'Automobil'"
										/>
									</div>

									<span>
										{{ ride.tax_model || "-" }}
									</span>
								</div>
							</td>

							<td>
								{{ ride.tax_plates || "-" }}
							</td>

							<td class="comment-cell">
								<span v-if="ride.rid_comment">
									{{ ride.rid_comment }}
								</span>

								<span v-else>-</span>
							</td>
						</table-row>
					</tbody>
				</table>
			</div>

			<div
				v-else
				class="no-result"
			>
				<h1>
					{{ $t("app.labels.noresult") }}
				</h1>
			</div>

			<div class="pagination-wrap">
				<pagination-comp
					:total-pages="pageCount"
					:current-page="currentPage"
					@page-changed="handlePageChange"
				/>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.content {
	width: 100%;
	padding: 30px;
	padding-left: 80px;
	padding-right: 80px;

	h2 {
		margin-bottom: 10px;
		font-size: 1.5rem;
		text-align: center;
	}

	.control-bar {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		gap: 24px;
		padding-left: 15px;
		padding-right: 15px;
		width: 100%;
		margin-bottom: 20px;

		.filters {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 24px;
		}

		.control-bar-item {
			display: flex;
			flex-direction: row;
			align-items: center;
			gap: 8px;

			label {
				flex-shrink: 0;
				white-space: nowrap;
			}
		}

		.multiselect-wrap {
			flex: 0 0 200px;
			width: 200px;
		}

		.search-wrap {
			flex: 0 0 337px;
			width: 337px;
		}
	}
}

.table-wrapper {
	width: 100%;
	overflow-x: auto;
	border: 1px solid rgba($app-accent-color1, 0.2);
	border-radius: 8px;
}

.rides-table {
	width: 100%;
	min-width: 1150px;
	border-collapse: collapse;
	font-family: Inter, sans-serif;
	font-size: 14px;
	font-style: normal;
	font-weight: 400;
	line-height: 150%;
	letter-spacing: 0;

	thead {
		background-color: rgba($app-accent-color1, 0.08);
	}

	th {
		padding: 14px 16px;
		font-family: Archivo, sans-serif;
		font-size: 16px;
		font-style: normal;
		font-weight: 600;
		line-height: 100%;
		letter-spacing: 0;
		text-align: left;
		white-space: nowrap;
	}
	// ID
	th:nth-child(1),
	td:nth-child(1) {
		min-width: 100px;
	}
}

.driver-info,
.vehicle-info {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 180px;
}

.table-image {
	display: flex;
	flex: 0 0 50px;
	justify-content: center;
	align-items: center;
	border-radius: 100%;
	width: 50px;
	height: 50px;
	overflow: hidden;
	background-color: rgba($app-accent-color1, 0.08);

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}
}

.vehicle-image {
	background-color: rgba($app-accent-color1, 0.12);
}

.image-placeholder {
	font-size: 13px;
	font-weight: 400;
	line-height: 1;
}

.driver-name {
	font: inherit;
	white-space: nowrap;
}

.comment-cell {
	width: 250px;
	max-width: 250px;

	span {
		display: block;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
}

.no-result {
	display: flex;
	justify-content: center;
	margin-top: 30px;
}

.pagination-wrap {
	position: fixed;
	bottom: 10px;
	left: 50%;
	display: flex;
	justify-content: center;
	width: fit-content;
	padding: 5px 10px;
	background: #ffffffd8;
	transform: translateX(-50%);
}

@media screen and (width <= 768px) {
	.content {
		padding: 15px;
	}

	.control-bar {
		align-items: stretch;

		.search-wrap {
			flex: none;
			width: 100%;
		}
	}

	.pagination-wrap {
		position: static;
		width: fit-content;
		margin: 20px auto 0;
		transform: none;
	}
}
</style>
